import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';

export interface CachedMediaEntry {
  key: string;
  url: string;
  objectUrl?: string | null;
  blob?: Blob | null;
  durationSeconds?: number | null;
}

@Injectable({ providedIn: 'root' })
export class MediaCacheService implements OnDestroy {
  private readonly entries = new Map<string, CachedMediaEntry>();
  private readonly inFlight = new Map<string, Promise<CachedMediaEntry>>();

  constructor(private readonly http: HttpClient) {}

  get(key: string): CachedMediaEntry | null {
    return this.entries.get(key) ?? null;
  }

  set(entry: CachedMediaEntry): void {
    const previous = this.entries.get(entry.key);
    if (previous?.objectUrl && previous.objectUrl !== entry.objectUrl) {
      URL.revokeObjectURL(previous.objectUrl);
    }
    this.entries.set(entry.key, entry);
  }

  /**
   * Drop a single cached entry (revoking its object URL) and any in-flight fetch for it. Needed when
   * the bytes behind `key` change but the key does NOT — e.g. a re-rendered Review video that
   * overwrites the same R2 storage key. Without this, `warmRemote` keeps serving the stale blob.
   */
  evict(key: string): void {
    this.inFlight.delete(key);
    const entry = this.entries.get(key);
    if (entry?.objectUrl) {
      try { URL.revokeObjectURL(entry.objectUrl); } catch { /* already revoked */ }
    }
    this.entries.delete(key);
  }

  /** Drop every cached entry (revoking object URLs). Coarse invalidation used after a re-render. */
  clearAll(): void {
    this.inFlight.clear();
    for (const entry of this.entries.values()) {
      if (entry.objectUrl) {
        try { URL.revokeObjectURL(entry.objectUrl); } catch { /* already revoked */ }
      }
    }
    this.entries.clear();
  }

  async warmRemote(key: string, url: string): Promise<CachedMediaEntry> {
    const cached = this.get(key);
    if (cached?.blob || cached?.objectUrl) return cached;

    const pending = this.inFlight.get(key);
    if (pending) return pending;

    const promise = firstValueFrom(this.http.get(url, { responseType: 'blob' }))
      .then(blob => {
        const objectUrl = URL.createObjectURL(blob);
        const entry: CachedMediaEntry = { key, url, objectUrl, blob };
        this.set(entry);
        return entry;
      })
      .finally(() => {
        this.inFlight.delete(key);
      });

    this.inFlight.set(key, promise);
    return promise;
  }

  ngOnDestroy(): void {
    this.inFlight.clear();
    for (const entry of this.entries.values()) {
      if (entry.objectUrl) URL.revokeObjectURL(entry.objectUrl);
    }
    this.entries.clear();
  }
}
